import React, { useState, useEffect } from 'react';
import { useRecruitment } from '../../context/RecruitmentContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { 
  Calendar, Clock, User, Briefcase, RefreshCw, Search, Filter,
  CheckCircle, XCircle, AlertCircle, PlayCircle, Pencil, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const statusColors = {
  'Scheduled': 'bg-blue-100 text-blue-700 border-blue-200',
  'Completed': 'bg-green-100 text-green-700 border-green-200',
  'Cancelled': 'bg-red-100 text-red-700 border-red-200',
  'No Show': 'bg-slate-100 text-slate-700 border-slate-200'
};

const recommendationColors = {
  'Strong Hire': 'bg-emerald-500 text-white',
  'Hire': 'bg-green-500 text-white',
  'No Hire': 'bg-red-500 text-white',
  'Hold': 'bg-amber-500 text-white'
};

const InterviewList = ({ onViewCandidate, getInterviewUrl, getCandidateUrl, initialEditInterviewId, onEditDialogClose }) => {
  const { fetchInterviews, fetchJobs, jobs, fetchCandidates, fetchAllCandidates, candidates, updateInterview, getCandidate } = useRecruitment();

  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('today');
  const [searchQuery, setSearchQuery] = useState('');
  const [jobFilter, setJobFilter] = useState('all');

  // Edit Interview dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingInterview, setEditingInterview] = useState(null);
  const [editingCandidateName, setEditingCandidateName] = useState('');
  const [editForm, setEditForm] = useState({
    round_name: '', round_number: 1, interviewer_name: '', scheduled_at: '', status: 'Scheduled'
  });
  const [editLoading, setEditLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  // Auto-open edit dialog when deep-linked
  useEffect(() => {
    if (!initialEditInterviewId || interviews.length === 0) return;
    const target = interviews.find(i => i.id === initialEditInterviewId);
    if (target) {
      openEditDialog(target);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialEditInterviewId, interviews.length]);

  const openEditDialog = async (interview) => {
    setEditingInterview(interview);
    // Resolve candidate name: try context cache first, fall back to API for deep-linked cases
    const cached = candidates.find(c => c.id === interview.candidate_id);
    if (cached) {
      setEditingCandidateName(cached.name);
    } else {
      setEditingCandidateName('');
      try {
        const c = await getCandidate(interview.candidate_id);
        if (c && c.name) setEditingCandidateName(c.name);
      } catch (e) {
        // ignore — description will gracefully show no name
      }
    }
    let dt = interview.scheduled_at || '';
    if (dt) {
      const d = new Date(dt);
      if (!isNaN(d)) {
        const pad = (n) => String(n).padStart(2, '0');
        dt = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
      }
    }
    setEditForm({
      round_name: interview.round_name || '',
      round_number: interview.round_number || 1,
      interviewer_name: interview.interviewer_name || '',
      scheduled_at: dt,
      status: interview.status || 'Scheduled'
    });
    setEditDialogOpen(true);
  };

  const handleEditDialogOpenChange = (open) => {
    setEditDialogOpen(open);
    if (!open) {
      setEditingInterview(null);
      setEditingCandidateName('');
      if (onEditDialogClose) onEditDialogClose();
    }
  };

  const handleSaveInterview = async () => {
    if (!editingInterview) return;
    if (!editForm.round_name || !editForm.scheduled_at) {
      toast.error('Round name and scheduled time are required');
      return;
    }
    setEditLoading(true);
    const result = await updateInterview(editingInterview.id, {
      candidate_id: editingInterview.candidate_id,
      job_id: editingInterview.job_id,
      ...editForm
    });
    setEditLoading(false);
    if (result.success) {
      toast.success('Interview updated');
      handleEditDialogOpenChange(false);
      loadData();
    } else {
      toast.error(result.error || 'Failed to update interview');
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [interviewsData] = await Promise.all([
        fetchInterviews(),
        fetchJobs(),
        fetchAllCandidates()
      ]);
      setInterviews(interviewsData || []);
    } catch (error) {
      console.error('Error loading interviews:', error);
      toast.error('Failed to load interviews');
    } finally {
      setLoading(false);
    }
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const categorizeInterviews = () => {
    const todayInterviews = [];
    const upcomingInterviews = [];
    const pastInterviews = [];

    interviews.forEach(interview => {
      const interviewDate = interview.scheduled_at ? new Date(interview.scheduled_at) : null;
      
      // Apply filters
      if (searchQuery) {
        const candidate = candidates.find(c => c.id === interview.candidate_id);
        const searchLower = searchQuery.toLowerCase();
        if (!candidate?.name?.toLowerCase().includes(searchLower) &&
            !interview.round_name?.toLowerCase().includes(searchLower) &&
            !interview.interviewer_name?.toLowerCase().includes(searchLower)) {
          return;
        }
      }
      
      if (jobFilter !== 'all' && interview.job_id !== jobFilter) {
        return;
      }

      if (!interviewDate) {
        upcomingInterviews.push(interview);
        return;
      }

      if (interviewDate >= today && interviewDate < tomorrow) {
        todayInterviews.push(interview);
      } else if (interviewDate >= tomorrow) {
        upcomingInterviews.push(interview);
      } else {
        pastInterviews.push(interview);
      }
    });

    // Sort by date
    todayInterviews.sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
    upcomingInterviews.sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
    pastInterviews.sort((a, b) => new Date(b.scheduled_at) - new Date(a.scheduled_at));

    return { todayInterviews, upcomingInterviews, pastInterviews };
  };

  const { todayInterviews, upcomingInterviews, pastInterviews } = categorizeInterviews();

  const getCandidateName = (candidateId) => {
    const candidate = candidates.find(c => c.id === candidateId);
    return candidate?.name || 'Unknown';
  };

  const getJobTitle = (jobId) => {
    const job = jobs.find(j => j.id === jobId);
    return job?.title || 'No Position';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'Cancelled':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'No Show':
        return <AlertCircle className="w-4 h-4 text-slate-500" />;
      default:
        return <PlayCircle className="w-4 h-4 text-blue-500" />;
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'TBD';
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    const date = new Date(dateString);
    return date.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const InterviewCard = ({ interview, showDate = false }) => {
    const candidate = candidates.find(c => c.id === interview.candidate_id);
    const candidateUrl = getCandidateUrl ? getCandidateUrl(interview.candidate_id) : '#';
    
    return (
      <Card className={`hover:shadow-md transition-shadow border-l-4 ${
        interview.status === 'Completed' ? 'border-l-green-500' :
        interview.status === 'Cancelled' ? 'border-l-red-500' :
        interview.status === 'No Show' ? 'border-l-slate-400' :
        'border-l-blue-500'
      }`} data-testid={`interview-card-${interview.id}`}>
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                {getStatusIcon(interview.status)}
                <a
                  href={getInterviewUrl ? getInterviewUrl(interview.id) : '#'}
                  onClick={(e) => {
                    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
                    e.preventDefault();
                    openEditDialog(interview);
                  }}
                  className="font-semibold hover:text-purple-600 hover:underline cursor-pointer"
                  data-testid={`interview-link-${interview.id}`}
                  title="Click to edit interview"
                >
                  {interview.round_name}
                </a>
                <Badge variant="outline">Round {interview.round_number}</Badge>
                <Badge className={statusColors[interview.status]}>{interview.status}</Badge>
              </div>
              
              <a
                href={candidateUrl}
                onClick={(e) => {
                  if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
                  e.preventDefault();
                  if (interview.candidate_id) onViewCandidate?.(interview.candidate_id);
                }}
                className="text-purple-600 font-medium cursor-pointer hover:underline inline-block"
                data-testid={`interview-card-candidate-${interview.id}`}
              >
                {candidate ? candidate.name : getCandidateName(interview.candidate_id)}
              </a>
              
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-slate-500">
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {formatTime(interview.scheduled_at)}
                </span>
                {showDate && (
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {formatDate(interview.scheduled_at)}
                  </span>
                )}
                {interview.interviewer_name && (
                  <span className="flex items-center gap-1">
                    <User className="w-3 h-3" />
                    {interview.interviewer_name}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <Briefcase className="w-3 h-3" />
                  {getJobTitle(interview.job_id)}
                </span>
              </div>

              {interview.recommendation && (
                <div className="mt-2">
                  <Badge className={recommendationColors[interview.recommendation]}>
                    {interview.recommendation}
                  </Badge>
                </div>
              )}

              {interview.feedback && (
                <p className="mt-2 text-sm text-slate-600 line-clamp-2">{interview.feedback}</p>
              )}
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 flex-shrink-0"
              onClick={() => openEditDialog(interview)}
              title="Edit interview"
              data-testid={`interview-edit-icon-${interview.id}`}
            >
              <Pencil className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  const EmptyState = ({ message, icon: Icon }) => (
    <div className="text-center py-12">
      <Icon className="w-12 h-12 mx-auto text-slate-300 mb-4" />
      <p className="text-slate-500">{message}</p>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Interviews</h2>
          <p className="text-slate-500">Manage and track all interview schedules</p>
        </div>
        <Button variant="outline" onClick={loadData}>
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by candidate, round or interviewer..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={jobFilter} onValueChange={setJobFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="All Jobs" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Jobs</SelectItem>
                {jobs.map(job => (
                  <SelectItem key={job.id} value={job.id}>{job.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Today</p>
                <p className="text-2xl font-semibold text-blue-600">{todayInterviews.length}</p>
              </div>
              <Calendar className="w-8 h-8 text-blue-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Upcoming</p>
                <p className="text-2xl font-semibold text-purple-600">{upcomingInterviews.length}</p>
              </div>
              <Clock className="w-8 h-8 text-purple-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Completed</p>
                <p className="text-2xl font-semibold text-green-600">
                  {pastInterviews.filter(i => i.status === 'Completed').length}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total</p>
                <p className="text-2xl font-semibold text-slate-600">{interviews.length}</p>
              </div>
              <Briefcase className="w-8 h-8 text-slate-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="today" className="gap-2">
            Today
            {todayInterviews.length > 0 && (
              <Badge variant="secondary" className="ml-1">{todayInterviews.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="upcoming" className="gap-2">
            Upcoming
            {upcomingInterviews.length > 0 && (
              <Badge variant="secondary" className="ml-1">{upcomingInterviews.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="past" className="gap-2">
            Past
            {pastInterviews.length > 0 && (
              <Badge variant="secondary" className="ml-1">{pastInterviews.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Today's Interviews */}
        <TabsContent value="today">
          {loading ? (
            <div className="flex justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : todayInterviews.length === 0 ? (
            <Card>
              <CardContent>
                <EmptyState 
                  message="No interviews scheduled for today" 
                  icon={Calendar} 
                />
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {todayInterviews.map(interview => (
                <InterviewCard key={interview.id} interview={interview} />
              ))}
            </div>
          )}
        </TabsContent>

        {/* Upcoming Interviews */}
        <TabsContent value="upcoming">
          {loading ? (
            <div className="flex justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : upcomingInterviews.length === 0 ? (
            <Card>
              <CardContent>
                <EmptyState 
                  message="No upcoming interviews scheduled" 
                  icon={Clock} 
                />
              </CardContent>
            </Card>
          ) : (
            <ScrollArea className="h-[500px]">
              <div className="space-y-3 pr-4">
                {upcomingInterviews.map(interview => (
                  <InterviewCard key={interview.id} interview={interview} showDate />
                ))}
              </div>
            </ScrollArea>
          )}
        </TabsContent>

        {/* Past Interviews */}
        <TabsContent value="past">
          {loading ? (
            <div className="flex justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : pastInterviews.length === 0 ? (
            <Card>
              <CardContent>
                <EmptyState 
                  message="No past interviews found" 
                  icon={CheckCircle} 
                />
              </CardContent>
            </Card>
          ) : (
            <ScrollArea className="h-[500px]">
              <div className="space-y-3 pr-4">
                {pastInterviews.map(interview => (
                  <InterviewCard key={interview.id} interview={interview} showDate />
                ))}
              </div>
            </ScrollArea>
          )}
        </TabsContent>
      </Tabs>

      {/* Edit Interview Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={handleEditDialogOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle data-testid="interview-dialog-title">Edit Interview</DialogTitle>
            <DialogDescription>
              Reschedule or modify this interview{editingCandidateName ? ` for ${editingCandidateName}` : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Interview Round *</Label>
              <Select 
                value={editForm.round_name} 
                onValueChange={(v) => setEditForm({ ...editForm, round_name: v })}
              >
                <SelectTrigger><SelectValue placeholder="Select round type..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Phone Screen">Phone Screen</SelectItem>
                  <SelectItem value="Technical Round">Technical Round</SelectItem>
                  <SelectItem value="Coding Round">Coding Round</SelectItem>
                  <SelectItem value="System Design">System Design</SelectItem>
                  <SelectItem value="HR Round">HR Round</SelectItem>
                  <SelectItem value="Manager Round">Manager Round</SelectItem>
                  <SelectItem value="Leadership Round">Leadership Round</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Round Number</Label>
                <Input
                  type="number"
                  min="1"
                  value={editForm.round_number}
                  onChange={(e) => setEditForm({ ...editForm, round_number: parseInt(e.target.value) || 1 })}
                />
              </div>
              <div className="space-y-2">
                <Label>Interviewer</Label>
                <Input
                  value={editForm.interviewer_name}
                  onChange={(e) => setEditForm({ ...editForm, interviewer_name: e.target.value })}
                  placeholder="Name"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Date & Time *</Label>
              <Input
                type="datetime-local"
                value={editForm.scheduled_at}
                onChange={(e) => setEditForm({ ...editForm, scheduled_at: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select 
                value={editForm.status} 
                onValueChange={(v) => setEditForm({ ...editForm, status: v })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Scheduled">Scheduled</SelectItem>
                  <SelectItem value="Completed">Completed</SelectItem>
                  <SelectItem value="Cancelled">Cancelled</SelectItem>
                  <SelectItem value="No Show">No Show</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => handleEditDialogOpenChange(false)}>Cancel</Button>
            <Button onClick={handleSaveInterview} disabled={editLoading} className="bg-purple-600 hover:bg-purple-700" data-testid="interview-dialog-save-btn">
              {editLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InterviewList;
